import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Check } from "lucide-react";
import { motion } from "framer-motion";
import { useProducts, money, cleanTitle } from "@/lib/api";
import { useCart, toLine } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { Reveal, SplitText } from "@/components/fx/Motion";

const pickPartners = (product, pool) => {
  const others = pool.filter((p) => p.handle !== product.handle && p.available && p.variants.length === 1 && p.price < 200);
  if (!others.length) return [];
  const seed = [...product.handle].reduce((s, c) => s + c.charCodeAt(0), 0);
  return [others[seed % others.length], others[(seed * 7 + 3) % others.length]].filter((p, i, a) => a.indexOf(p) === i).slice(0, 2);
};

const Item = ({ p, on, toggle, current, t }) => (
  <motion.button type="button" data-testid={`fbt-item-${p.handle}`} onClick={toggle} disabled={current} whileHover={{ y: -6 }}
    className={`relative text-left flex-1 min-w-[104px] sm:min-w-[140px] border p-3 transition-colors ${on ? "border-[#c9a55c]" : "border-white/10 opacity-60"}`}>
    <span className={`absolute top-2 right-2 h-6 w-6 flex items-center justify-center border z-10 ${on ? "bg-gold border-transparent text-[#0b0a08]" : "border-white/30"}`}>{on && <Check size={14} />}</span>
    <div className="aspect-square bg-white mb-3"><img src={p.images[0]} alt="" className="h-full w-full object-contain p-2" /></div>
    <p className="text-[10px] uppercase tracking-[0.15em] text-gold min-h-4 truncate">{current ? t("fbt_this_item") : "\u00a0"}</p>
    <p className="text-xs leading-snug line-clamp-2">{cleanTitle(p.title)}</p>
    <p className="text-sm font-semibold mt-1">{money(p.price)}</p>
  </motion.button>
);

export const FrequentlyBought = ({ product }) => {
  const { t } = useI18n();
  const { data: pool = [] } = useProducts({});
  const { add, checkout } = useCart();
  const partners = useMemo(() => pickPartners(product, pool), [product, pool]);
  const [off, setOff] = useState({});
  useEffect(() => { setOff({}); }, [product.handle]);
  if (!partners.length || product.variants.length > 1) return null;

  const all = [product, ...partners];
  const chosen = all.filter((p) => !off[p.handle]);
  const lines = chosen.map((p) => toLine(p, p.variants[0]));
  const total = chosen.reduce((s, p) => s + p.price, 0);

  return (
    <section data-testid="fbt-section" className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
      <p className="eyebrow mb-4">{t("fbt_eyebrow")}</p>
      <h2 className="font-display text-3xl lg:text-4xl mb-10"><SplitText text={t("fbt_title")} /></h2>
      <div className="grid lg:grid-cols-[1fr_320px] gap-10 items-center">
        <Reveal className="flex items-center gap-2 sm:gap-4 overflow-x-auto no-scrollbar">
          {all.map((p, i) => (
            <div key={p.handle} className="flex items-center gap-2 sm:gap-4 flex-1">
              {i > 0 && <Plus size={18} className="text-gold shrink-0" />}
              <Item p={p} t={t} current={i === 0} on={!off[p.handle]} toggle={() => setOff((o) => ({ ...o, [p.handle]: !o[p.handle] }))} />
            </div>
          ))}
        </Reveal>
        <Reveal delay={0.2} className="border border-white/10 p-6">
          <p className="text-xs text-white/50">{t("fbt_total", { n: chosen.length })}</p>
          <p data-testid="fbt-total" className="text-3xl font-semibold mt-1 mb-5">{money(total)}</p>
          <button data-testid="fbt-buy-button" onClick={() => checkout(lines)} className="btn-gold btn-shine w-full h-14">{t("fbt_buy")}</button>
          <button data-testid="fbt-add-button" onClick={() => lines.forEach((l, i) => add(l, i === lines.length - 1, i === lines.length - 1))} className="btn-ghost w-full min-h-12 py-3 px-3 mt-3 text-[10px] leading-snug">{t("fbt_add")}</button>
          <Link to={`/products/${partners[0].handle}`} className="sr-only">{partners[0].title}</Link>
        </Reveal>
      </div>
    </section>
  );
};
